// i18n entrypoint — initialises i18next with the 9 supported locales.
//
// Locale resolution priority (highest → lowest):
//   1. URL path prefix: /es/foo, /fr/foo, …  (set by the wouter <Route base>)
//   2. localStorage `<domain>:lang` (set by the in-app switcher)
//   3. <html lang> attribute (in case it was set by SSR / static HTML)
//   4. browser Accept-Language
//   5. fallback: en
//
// All locale JSONs are bundled at build time so language switching is
// immediate; no runtime fetch.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { config } from '../lib/config';

import en from './locales/en.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import ar from './locales/ar.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import sv from './locales/sv.json';
import ja from './locales/ja.json';
import ms from './locales/ms.json';

export const SUPPORTED_LANGS = config.app.locales as readonly string[];
export type SupportedLang = string;

const RTL_LANGS = new Set<SupportedLang>(['ar']);

const STORAGE_KEY = `${config.app.domain}:lang`;

/** Read the language code from the first segment of the URL path. */
export function langFromPath(): SupportedLang | null {
  const seg = window.location.pathname.split('/').filter(Boolean)[0];
  if (seg && (SUPPORTED_LANGS as readonly string[]).includes(seg)) {
    return seg as SupportedLang;
  }
  return null;
}

/** Read the language from localStorage, validating against the allow-list. */
function langFromStorage(): SupportedLang | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && (SUPPORTED_LANGS as readonly string[]).includes(v)) return v as SupportedLang;
  } catch { /* private browsing */ }
  return null;
}

/** Best-effort detection from the browser's Accept-Language. */
function langFromBrowser(): SupportedLang {
  const raw = navigator.language?.toLowerCase() ?? config.app.defaultLocale;
  const code = raw.split('-')[0];
  if ((SUPPORTED_LANGS as readonly string[]).includes(code)) return code as SupportedLang;
  return config.app.defaultLocale as SupportedLang;
}

export function detectInitialLanguage(): SupportedLang {
  return langFromPath() ?? langFromStorage() ?? langFromBrowser();
}

export function setHtmlAttributes(lang: SupportedLang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
}

/** Persist the user's language choice. */
export function persistLanguage(lang: SupportedLang): void {
  try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
}

const initial = detectInitialLanguage();
setHtmlAttributes(initial);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
    pt: { translation: pt },
    ar: { translation: ar },
    de: { translation: de },
    fr: { translation: fr },
    sv: { translation: sv },
    ja: { translation: ja },
    ms: { translation: ms },
  },
  lng: initial,
  fallbackLng: config.app.defaultLocale,
  interpolation: { escapeValue: false }, // React handles escaping
  returnNull: false,
});

i18n.on('languageChanged', (lng) => {
  if ((SUPPORTED_LANGS as readonly string[]).includes(lng)) {
    setHtmlAttributes(lng as SupportedLang);
    persistLanguage(lng as SupportedLang);
  }
});

export default i18n;

/**
 * Returns the version of `path` prefixed with the active language code,
 * unless the active language is English (which lives at the root).
 *
 *   localizedPath('/event/2026-mv-hondius', 'es') → '/es/event/2026-mv-hondius'
 *   localizedPath('/event/2026-mv-hondius', 'en') → '/event/2026-mv-hondius'
 */
export function localizedPath(path: string, lang: SupportedLang): string {
  if (lang === 'en') return path;
  if (path === '/' || path === '') return `/${lang}`;
  return `/${lang}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Returns `path` with any language prefix stripped, so it can be re-prefixed
 * for a different language.
 *
 *   stripLangPrefix('/es/event/x') → '/event/x'
 *   stripLangPrefix('/event/x')    → '/event/x'
 */
export function stripLangPrefix(path: string): string {
  const segments = path.split('/');
  const first = segments[1];
  if (first && (SUPPORTED_LANGS as readonly string[]).includes(first) && first !== 'en') {
    return '/' + segments.slice(2).join('/');
  }
  return path;
}
